/**
 * Detect Based app hosts vs customer custom domains (visitor surfaces).
 */

const BASED_APP_HOSTS = new Set([
  'trybased.com',
  'www.trybased.com',
  'crm.trybased.com',
  'mcp.trybased.com',
  'localhost',
  '127.0.0.1',
]);

export function getPageHostname() {
  if (typeof window === 'undefined') return 'trybased.com';
  return (window.location.hostname || '').toLowerCase().replace(/\.$/, '');
}

export function isBasedAppHostname(hostname = getPageHostname()) {
  const h = String(hostname || '').toLowerCase().replace(/\.$/, '');
  if (!h) return true;
  if (BASED_APP_HOSTS.has(h)) return true;
  if (h.endsWith('.vercel.app')) return true;
  return false;
}

export function isCustomDomainHost(hostname = getPageHostname()) {
  return !isBasedAppHostname(hostname);
}

/** Hide “Powered by Based” and restore Based favicon on customer hosts. */
export function shouldShowBasedAttribution(hostname = getPageHostname()) {
  return isBasedAppHostname(hostname);
}

/**
 * Default public-share origin when the org has no active custom domain.
 * Prefers the current page origin (www.trybased.com / localhost) so copy/paste
 * matches what the admin is browsing; falls back to production.
 */
export function getDefaultPublicShareOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://trybased.com';
}

function activeCustomOrigin(customDomain) {
  const hostname = String(customDomain?.hostname || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if (
    hostname
    && customDomain?.status === 'active'
    && !isBasedAppHostname(hostname)
  ) {
    return `https://${hostname}`;
  }
  return null;
}

/**
 * Origin for public `/shared/:id` URLs shown in Share UI.
 * Only an *active* shared (docs.*) custom domain rewrites the host; pending DNS /
 * missing / disabled keep the default trybased (or localhost) origin.
 *
 * Prefer passing the shared-purpose row (`sharedDomain` / `domain` from settings).
 * Do not pass a mascot.* host here.
 *
 * @param {{ hostname?: string, status?: string, purpose?: string } | null | undefined} customDomain
 * @returns {string} origin with scheme, no trailing slash
 */
export function resolvePublicShareOrigin(customDomain = null) {
  // Guard: never use a mascot host for /shared links
  const host = String(customDomain?.hostname || '').toLowerCase();
  if (host.startsWith('mascot.') || customDomain?.purpose === 'mascot') {
    return getDefaultPublicShareOrigin();
  }
  return activeCustomOrigin(customDomain) || getDefaultPublicShareOrigin();
}

/** True for customer mascot hosts such as mascot.stomachman.com. */
export function isMascotCustomHostname(hostname = getPageHostname()) {
  return String(hostname || '').toLowerCase().replace(/\.$/, '').startsWith('mascot.');
}

/**
 * Origin for public mascot URLs when an active mascot.* domain exists.
 * Falls back to www.trybased.com (or localhost in local dev).
 * Never returns a docs.* host.
 * On a mascot.* host the path is `/{id}`; on trybased it stays `/mascot/{id}`.
 *
 * @param {{ hostname?: string, status?: string, purpose?: string } | null | undefined} mascotDomain
 * @returns {string}
 */
export function resolveMascotShareOrigin(mascotDomain = null) {
  const host = String(mascotDomain?.hostname || '').toLowerCase();
  if (host.startsWith('docs.') || mascotDomain?.purpose === 'shared') {
    // Fall through to default — never put mascot on docs host
  } else {
    const active = activeCustomOrigin(mascotDomain);
    if (active) return active;
  }
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return origin;
    }
  }
  return 'https://www.trybased.com';
}

const RESOLVE_URL =
  'https://us-central1-aiknowledgebase-7427e.cloudfunctions.net/resolveCustomDomain';

/**
 * Resolve apex custom domain → { hostname, purpose, apex, active }.
 * @param {string} [hostOverride] optional host for local testing (?host=)
 */
export async function fetchCustomDomainResolution(hostOverride) {
  const host = (hostOverride || getPageHostname() || '').trim().toLowerCase();
  if (!host || isBasedAppHostname(host)) {
    throw new Error('Not a custom domain');
  }
  const url = `${RESOLVE_URL}?host=${encodeURIComponent(host)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Custom domain not found');
  }
  return res.json();
}
